import { useEffect, useState } from 'react';
import { REALTIME_SUBSCRIBE_STATES } from '@supabase/supabase-js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import {
  addTaskCollaborator,
  createSubtask,
  createTask,
  deleteTask,
  fetchProjectTasks,
  fetchTask,
  fetchTaskActivity,
  fetchTaskCollaborators,
  removeTaskCollaborator,
  reorderTasks,
  setTaskCompleted,
  updateTask,
} from './queries';
import type { CreateSubtaskInput, CreateTaskInput, ProjectTasks, TaskUpdate } from './types';
import { useWorkspaceSettings } from '../settings/hooks';
import { defaultDueDate } from './taskLens';

/**
 * Server state for tasks. TanStack Query is the only server state layer;
 * there are no Realtime subscriptions and nothing is prefetched at startup.
 * Activity history has its own key so a task panel can load it separately.
 */

export const taskKeys = {
  all: ['tasks'] as const,
  project: (projectId: string) => [...taskKeys.all, 'project', projectId] as const,
  detail: (taskId: string) => [...taskKeys.all, 'detail', taskId] as const,
  activity: (taskId: string) => [...taskKeys.all, 'activity', taskId] as const,
  collaborators: (taskId: string) => [...taskKeys.all, 'collaborators', taskId] as const,
};

/** The signed-in user's id, or an empty string when access is not approved. */
function useActorId(): string {
  const { access } = useAuth();
  return access.status === 'approved' ? access.profile.id : '';
}

/** The fallback cadence when the realtime channel is not delivering. */
export const TASKS_FALLBACK_POLL_MS = 30_000;

export function useProjectTasks(projectId: string | undefined, refetchInterval?: number) {
  const { client } = useAuth();
  return useQuery({
    queryKey: taskKeys.project(projectId ?? ''),
    queryFn: () => fetchProjectTasks(client, projectId as string),
    enabled: Boolean(projectId),
    // Authorization is re-resolved by the request, so a stale authorized
    // result must never satisfy a later load.
    retry: false,
    // Polling is the fallback, not the mechanism: the caller passes an
    // interval only while the realtime channel is not live (handoff 39).
    ...(refetchInterval !== undefined ? { refetchInterval } : {}),
  });
}

export type TasksRealtimeStatus = 'connecting' | 'live' | 'offline';

/**
 * Selective realtime (Phase 5 Stage 3, handoff 39): exactly one channel, on
 * exactly the open project's tasks, closed when the caller unmounts or moves
 * to another project. A delivered change invalidates the cached list — the
 * refetch is an ordinary authorized read, so realtime never becomes a second
 * source of truth. Supabase only broadcasts rows the subscriber could SELECT
 * under Row Level Security.
 *
 * The returned status lets the caller keep polling while the channel is not
 * live ('offline' also covers projects on a database where tasks is not in
 * the publication — everything still works, just on the polling cadence).
 */
export function useTasksRealtime(projectId: string | undefined): TasksRealtimeStatus {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<TasksRealtimeStatus>('connecting');

  useEffect(() => {
    if (!projectId) {
      return;
    }
    setStatus('connecting');
    const channel = client
      .channel(`tasks-${projectId.toLowerCase()}`, { config: { private: true } })
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tasks', filter: `project_id=eq.${projectId}` },
        () => {
          void queryClient.invalidateQueries({ queryKey: taskKeys.project(projectId) });
        },
      )
      .subscribe((state) => {
        if (state === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
          setStatus('live');
        } else if (
          state === REALTIME_SUBSCRIBE_STATES.CHANNEL_ERROR ||
          state === REALTIME_SUBSCRIBE_STATES.TIMED_OUT ||
          state === REALTIME_SUBSCRIBE_STATES.CLOSED
        ) {
          setStatus('offline');
        }
      });

    return () => {
      void client.removeChannel(channel);
    };
  }, [client, queryClient, projectId]);

  return status;
}

export function useTask(taskId: string | undefined) {
  const { client } = useAuth();
  return useQuery({
    queryKey: taskKeys.detail(taskId ?? ''),
    queryFn: () => fetchTask(client, taskId as string),
    enabled: Boolean(taskId),
    retry: false,
  });
}

/** Loaded only when a task panel opens, never with the task list. */
export function useTaskActivity(taskId: string | undefined) {
  const { client } = useAuth();
  return useQuery({
    queryKey: taskKeys.activity(taskId ?? ''),
    queryFn: () => fetchTaskActivity(client, taskId as string),
    enabled: Boolean(taskId),
    retry: false,
  });
}

export function useTaskCollaborators(taskId: string | undefined) {
  const { client } = useAuth();
  return useQuery({
    queryKey: taskKeys.collaborators(taskId ?? ''),
    queryFn: () => fetchTaskCollaborators(client, taskId as string),
    enabled: Boolean(taskId),
    retry: false,
  });
}

export function useCreateTask(projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const createdBy = useActorId();
  return useMutation({
    mutationFn: (input: CreateTaskInput) => createTask(client, { ...input, createdBy }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: taskKeys.project(projectId) });
    },
  });
}

export function useCreateSubtask(projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const createdBy = useActorId();
  return useMutation({
    mutationFn: (input: CreateSubtaskInput) => createSubtask(client, { ...input, createdBy }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: taskKeys.project(projectId) });
    },
  });
}

/**
 * A task edit. Activity history is written by the database, so the activity
 * key is invalidated alongside the task itself rather than written to here.
 */
export function useUpdateTask(projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, changes }: { taskId: string; changes: TaskUpdate }) =>
      updateTask(client, taskId, changes),
    onSuccess: (task) => {
      void queryClient.invalidateQueries({ queryKey: taskKeys.project(projectId) });
      void queryClient.invalidateQueries({ queryKey: taskKeys.detail(task.id) });
      void queryClient.invalidateQueries({ queryKey: taskKeys.activity(task.id) });
    },
  });
}

/**
 * A status move from the Board (Phase 5 Stage 2). The only optimistic
 * mutation in the app: a dragged card must land in its column immediately,
 * so the cached project list is patched up front and restored if the write
 * fails. The database still decides — Row Level Security refuses a move the
 * caller may not make, and the rollback puts the card back where it was.
 */
export function useMoveTaskStatus(projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      taskId,
      statusId,
      isCompleted,
    }: {
      taskId: string;
      statusId: string | null;
      /**
       * Set only by the unified board, where a column carries completion as
       * well as status and the two are written together. Omitted elsewhere,
       * which leaves completion exactly as it was — the ordinary project board
       * still does not complete anything by dragging.
       *
       * completed_at and completed_by are NOT sent: tasks_maintain_timestamps()
       * derives both from is_completed, and the table's own CHECK refuses a
       * pair that disagrees. Sending them from here would be asking the client
       * to be honest about when something finished.
       */
      isCompleted?: boolean;
    }) =>
      updateTask(
        client,
        taskId,
        isCompleted === undefined
          ? { status_id: statusId }
          : { status_id: statusId, is_completed: isCompleted },
      ),
    onMutate: async ({ taskId, statusId, isCompleted }) => {
      const key = taskKeys.project(projectId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<ProjectTasks>(key);
      if (previous) {
        queryClient.setQueryData<ProjectTasks>(key, {
          ...previous,
          tasks: previous.tasks.map((task) =>
            task.id === taskId
              ? {
                  ...task,
                  status_id: statusId,
                  ...(isCompleted === undefined ? {} : { is_completed: isCompleted }),
                }
              : task,
          ),
        });
      }
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) {
        queryClient.setQueryData(taskKeys.project(projectId), context.previous);
      }
    },
    onSettled: (task) => {
      void queryClient.invalidateQueries({ queryKey: taskKeys.project(projectId) });
      if (task) {
        void queryClient.invalidateQueries({ queryKey: taskKeys.detail(task.id) });
        void queryClient.invalidateQueries({ queryKey: taskKeys.activity(task.id) });
      }
    },
  });
}

/** Completion only. Status is changed through useUpdateTask; the two are independent. */
export function useSetTaskCompleted(projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, isCompleted }: { taskId: string; isCompleted: boolean }) =>
      setTaskCompleted(client, taskId, isCompleted),
    onSuccess: (task) => {
      void queryClient.invalidateQueries({ queryKey: taskKeys.project(projectId) });
      void queryClient.invalidateQueries({ queryKey: taskKeys.detail(task.id) });
      void queryClient.invalidateQueries({ queryKey: taskKeys.activity(task.id) });
    },
  });
}

/**
 * Position changes only. The task list re-sorts on the invalidated refetch,
 * so nothing is written optimistically here either.
 */
/**
 * Reordering, optimistically.
 *
 * A dropped row settles where it was dropped rather than after the round trip.
 * Only positions change here — display order is derived from them at render,
 * so patching the numbers is enough and the rows re-sort themselves.
 *
 * ProjectTasks is an object around the array, so this patches the shape
 * directly rather than using the shared patchCachedRows helper.
 */
export function useReorderTasks(projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (updates: { id: string; position: number }[]) => reorderTasks(client, updates),
    onMutate: async (updates) => {
      const key = taskKeys.project(projectId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<ProjectTasks>(key);
      if (previous) {
        const byId = new Map(updates.map((update) => [update.id, update.position]));
        queryClient.setQueryData<ProjectTasks>(key, {
          ...previous,
          tasks: previous.tasks.map((task) => {
            const position = byId.get(task.id);
            return position === undefined ? task : { ...task, position };
          }),
        });
      }
      return { previous };
    },
    onError: (_error, _updates, context) => {
      if (context?.previous) {
        queryClient.setQueryData(taskKeys.project(projectId), context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: taskKeys.project(projectId) });
    },
  });
}

/**
 * Deleting a task, optimistically.
 *
 * The row leaves the list the moment the confirmation is accepted, and comes
 * back if the write is refused. This matters most on Personal Tasks, where
 * somebody deleting their own note has no reason to wait on a round trip for
 * something only they can see.
 *
 * ProjectTasks is an object with a tasks array rather than a bare array, so
 * the shared removeCachedRow helper does not fit; the shape is patched here
 * and restored wholesale on failure, which is the same contract.
 */
export function useDeleteTask(projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => deleteTask(client, taskId),
    onMutate: async (taskId) => {
      const key = taskKeys.project(projectId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<ProjectTasks>(key);
      if (previous) {
        queryClient.setQueryData<ProjectTasks>(key, {
          ...previous,
          // Subtasks go with their parent: the database cascades them, and
          // leaving orphans on screen would show rows that no longer exist.
          tasks: previous.tasks.filter(
            (task) => task.id !== taskId && task.parent_task_id !== taskId,
          ),
        });
      }
      return { previous };
    },
    onError: (_error, _taskId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(taskKeys.project(projectId), context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: taskKeys.project(projectId) });
    },
  });
}

export function useAddTaskCollaborator(taskId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const addedBy = useActorId();
  return useMutation({
    mutationFn: (userId: string) => addTaskCollaborator(client, { taskId, userId, addedBy }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: taskKeys.collaborators(taskId) });
    },
  });
}

export function useRemoveTaskCollaborator(taskId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (collaboratorId: string) => removeTaskCollaborator(client, collaboratorId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: taskKeys.collaborators(taskId) });
    },
  });
}

/**
 * The workspace rules that apply to a new task.
 *
 * One hook rather than three components each reaching for useWorkspaceSettings
 * and useAuth and doing their own arithmetic — the quick-add row, the subtask
 * form and the deal-task form must agree, and three copies of the same three
 * lines is how they stop agreeing.
 *
 * While the settings query is still in flight this returns no default and no
 * requirement. That is the right way round: seeding a date a moment after the
 * form appears would overwrite something somebody had already typed, and
 * enforcing a rule before it is known would block a save that is allowed.
 */
export function useTaskDefaults(): { defaultDueDate: string | null; requireDueDate: boolean } {
  const { access } = useAuth();
  const settings = useWorkspaceSettings();
  const timeZone = access.status === 'approved' ? access.profile.time_zone : 'UTC';
  const workspace = settings.data ?? null;
  if (workspace === null) {
    return { defaultDueDate: null, requireDueDate: false };
  }
  return {
    defaultDueDate: defaultDueDate(workspace.default_task_due_days, timeZone),
    requireDueDate: workspace.require_due_date_on_tasks,
  };
}
